import React, { useState, useEffect, useRef } from "react";
import { Animated, StyleSheet, View } from "react-native";
import { colors, layout, shadows } from "@/constants";
import { onToast } from "@/utils/toast";
import Icon from "./Icon";
import Text from "./Text";

const DISPLAY_DURATION = 10000;
const FADE_DURATION = 200;

/**
 * Global transient notification for background action failures (e.g., delete errors).
 * Auto-dismisses after 10 seconds.
 * Mount once at app root. Driven by showToast() from utils/toast.
 *
 * For inline form/view-level errors, use NotificationCard
 * with variant="error" instead.
 */
export default function Toast() {
  const [message, setMessage] = useState<string | null>(null);
  const opacity = useRef(new Animated.Value(0)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return onToast((msg) => {
      // Clear any existing timer
      if (timerRef.current) clearTimeout(timerRef.current);

      setMessage(msg);
      Animated.timing(opacity, {
        toValue: 1,
        duration: FADE_DURATION,
        useNativeDriver: true
      }).start();

      timerRef.current = setTimeout(() => {
        Animated.timing(opacity, {
          toValue: 0,
          duration: FADE_DURATION,
          useNativeDriver: true
        }).start(() => setMessage(null));
      }, DISPLAY_DURATION);
    });
  }, [opacity]);

  if (!message) return null;

  return (
    <Animated.View style={[styles.container, { opacity }]}>
      <View style={styles.content}>
        <Icon name="error" size="medium" style={{ color: colors.error }} />
        <Text size="small" style={styles.text} numberOfLines={2}>
          {message}
        </Text>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: layout.space.xl * 3,
    left: layout.space.lg,
    right: layout.space.lg,
    alignItems: "center",
    pointerEvents: "none"
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.sm,
    backgroundColor: colors.inverseSurface,
    borderRadius: layout.borderRadius.large,
    padding: layout.space.md,
    ...shadows.raised
  },
  text: {
    color: colors.inverseOnSurface,
    flex: 1
  }
});
